import { useCallback, useEffect, useState } from "react";
import { Loader2, Plus, Pencil, Trash2, ArrowUp, ArrowDown, X, Image as ImageIcon, History } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr } from "./api";
import { SCHEMAS as SCHEMAS_REF } from "./contentSchemas";
import { imgUrl } from "../data/content";

const inputCls = "w-full border border-slate-300 px-3.5 py-2.5 text-sm rounded-sm focus:outline-none focus:border-[#C5221F]";
const labelCls = "block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5";

const ImageField = ({ value, onChange, testid }) => {
  const [media, setMedia] = useState(null);
  return (
    <div>
      <div className="flex gap-2">
        <input className={inputCls} value={value || ""} data-testid={testid}
          placeholder="/api/uploads/… or asset name (e.g. hero-panipat)"
          onChange={(e) => onChange(e.target.value)} />
        <button type="button" data-testid={`${testid}-browse`}
          className="shrink-0 inline-flex items-center gap-1.5 border border-slate-300 hover:border-[#C5221F] px-3 text-[11px] font-semibold rounded-sm"
          onClick={async () => {
            if (media) { setMedia(null); return; }
            try { const { data } = await adminApi.get("/media"); setMedia(Array.isArray(data) ? data : []); }
            catch (e) { toast.error(fmtErr(e)); }
          }}>
          <ImageIcon size={13} /> {media ? "Close" : "Browse"}
        </button>
      </div>
      {value ? <img src={imgUrl(value)} alt="" className="mt-2 h-20 w-auto object-contain border border-slate-200 bg-slate-50" /> : null}
      {media && (
        <div className="mt-2 grid grid-cols-4 gap-2 max-h-48 overflow-y-auto border border-slate-200 p-2 bg-slate-50" data-testid={`${testid}-media-picker`}>
          {media.map((m) => (
            <button type="button" key={m.id} onClick={() => { onChange(m.url); setMedia(null); }}
              className="border border-transparent hover:border-[#C5221F] bg-white p-1" title={m.title || m.filename}>
              <img src={m.thumb_url || m.url} alt={m.alt || ""} className="h-14 w-full object-cover" />
            </button>
          ))}
          {media.length === 0 && <p className="col-span-4 text-xs text-slate-500 p-2">Media library is empty — upload via Media Library first.</p>}
        </div>
      )}
    </div>
  );
};

const PairsField = ({ value, onChange, testid }) => {
  const rows = Array.isArray(value) ? value : [];
  const set = (i, j, v) => { const n = rows.map((r, x) => (x === i ? (j === 0 ? [v, r[1]] : [r[0], v]) : r)); onChange(n); };
  return (
    <div className="space-y-2" data-testid={testid}>
      {rows.map((r, i) => (
        <div key={i} className="flex gap-2">
          <input className={inputCls} value={r[0] || ""} placeholder="Label" onChange={(e) => set(i, 0, e.target.value)} />
          <input className={inputCls} value={r[1] || ""} placeholder="Value" onChange={(e) => set(i, 1, e.target.value)} />
          <button type="button" onClick={() => onChange(rows.filter((_, x) => x !== i))} className="shrink-0 p-2 text-slate-400 hover:text-[#C5221F]"><X size={14} /></button>
        </div>
      ))}
      <button type="button" onClick={() => onChange([...rows, ["", ""]])} data-testid={`${testid}-add`}
        className="text-[11px] font-semibold text-[#C5221F] hover:underline">+ Add row</button>
    </div>
  );
};

const Field = ({ f, value, onChange, idPrefix }) => {
  const testid = `${idPrefix}-${f.key}`;
  if (f.kind === "textarea")
    return <textarea rows={4} className={inputCls} data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
  if (f.kind === "toggle")
    return (
      <label className="flex items-center gap-2.5 text-sm text-slate-700 cursor-pointer">
        <input type="checkbox" className="w-4 h-4 accent-[#C5221F]" data-testid={testid} checked={!!value} onChange={(e) => onChange(e.target.checked)} />
        {value ? "Yes" : "No"}
      </label>
    );
  if (f.kind === "list")
    return <textarea rows={4} className={inputCls} data-testid={testid} value={(value || []).join("\n")} onChange={(e) => onChange(e.target.value.split("\n").map((s) => s.trim()).filter(Boolean))} />;
  if (f.kind === "pairs") return <PairsField value={value} onChange={onChange} testid={testid} />;
  if (f.kind === "image") return <ImageField value={value} onChange={onChange} testid={testid} />;
  if (f.kind === "select")
    return (
      <select className={`${inputCls} bg-white`} data-testid={testid} value={value ?? f.options[0]} onChange={(e) => onChange(e.target.value)}>
        {f.options.map((o) => <option key={o} value={o}>{o || "— none —"}</option>)}
      </select>
    );
  return <input className={inputCls} data-testid={testid} value={value || ""} onChange={(e) => onChange(e.target.value)} />;
};

const StatusBadge = ({ status }) => (
  <span className={`inline-block border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider ${status === "published" ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
    {status}
  </span>
);

const ContentManager = ({ type, title, description }) => {
  const [items, setItems] = useState(null);
  const [error, setError] = useState("");
  const [editing, setEditing] = useState(null); // doc being edited
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [revs, setRevs] = useState(null);
  const schema = SCHEMAS_REF[type];

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get(`/content/${type}`);
      setItems(Array.isArray(data) ? data : []);
    } catch (e) { setError(fmtErr(e)); }
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const openEdit = (doc) => { setForm({ ...(doc?.data || {}) }); setEditing(doc || { id: null, data: {}, order: (items?.length || 0) * 10 }); setRevs(null); };
  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (publishAfter) => {
    setBusy(true);
    try {
      let doc;
      if (editing.id) {
        ({ data: doc } = await adminApi.put(`/content/${type}/${editing.id}`, { data: form, order: editing.order ?? 0 }));
      } else {
        ({ data: doc } = await adminApi.post(`/content/${type}`, { data: form, order: editing.order ?? 0 }));
      }
      if (publishAfter) await adminApi.post(`/content/${type}/${doc.id}/publish`);
      toast.success(publishAfter ? "Saved and published." : "Draft saved.");
      setEditing(null);
      await load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  const move = async (i, dir) => {
    const ids = items.map((x) => x.id);
    const j = i + dir;
    if (j < 0 || j >= ids.length) return;
    [ids[i], ids[j]] = [ids[j], ids[i]];
    setItems(ids.map((id) => items.find((x) => x.id === id)));
    try { await adminApi.post(`/content/${type}/reorder`, { ids }); } catch (e) { toast.error(fmtErr(e)); load(); }
  };

  const remove = async (doc) => {
    if (!window.confirm(`Delete "${doc.data?.[schema.nameField] || doc.id}"? This cannot be undone.`)) return;
    try { await adminApi.delete(`/content/${type}/${doc.id}`); toast.success("Deleted."); load(); }
    catch (e) { toast.error(fmtErr(e)); }
  };

  const togglePublish = async (doc) => {
    try {
      await adminApi.post(`/content/${type}/${doc.id}/${doc.status === "published" ? "unpublish" : "publish"}`);
      load();
    } catch (e) { toast.error(fmtErr(e)); }
  };

  if (items === null) return <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>;
  if (error) return <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm" data-testid={`${type}-error`}>{error}</p>;

  return (
    <div data-testid={`cm-${type}`}>
      <div className="flex items-end justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-display text-xl font-bold text-slate-900">{title}</h2>
          {description && <p className="mt-1 text-sm text-slate-500">{description}</p>}
        </div>
        <button onClick={() => openEdit(null)} data-testid={`cm-${type}-add`}
          className="inline-flex items-center gap-1.5 bg-[#C5221F] hover:bg-[#A81B18] text-white font-display font-semibold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors">
          <Plus size={13} /> Add Item
        </button>
      </div>
      <div className="mt-6 bg-white border border-slate-200" data-testid={`cm-${type}-list`}>
        {items.length === 0 && <p className="p-6 text-sm text-slate-500">No items yet.</p>}
        {items.map((doc, i) => (
          <div key={doc.id} className="flex items-center gap-3 px-4 py-3.5 border-b border-slate-100 last:border-b-0" data-testid={`cm-row-${doc.id}`}>
            <div className="flex flex-col">
              <button onClick={() => move(i, -1)} disabled={i === 0} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`cm-up-${doc.id}`}><ArrowUp size={13} /></button>
              <button onClick={() => move(i, 1)} disabled={i === items.length - 1} className="text-slate-400 hover:text-slate-700 disabled:opacity-30" data-testid={`cm-down-${doc.id}`}><ArrowDown size={13} /></button>
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-slate-800 truncate">{doc.data?.[schema.nameField] || doc.id}</p>
              <p className="text-[11px] text-slate-400 font-mono-tech">{doc.id}{doc.data?.active === false ? " · hidden" : ""}</p>
            </div>
            <StatusBadge status={doc.status} />
            <button onClick={() => togglePublish(doc)} data-testid={`cm-publish-${doc.id}`} className="text-[11px] font-semibold text-slate-500 hover:text-[#C5221F]">
              {doc.status === "published" ? "Unpublish" : "Publish"}
            </button>
            <button onClick={() => openEdit(doc)} data-testid={`cm-edit-${doc.id}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-[#0A1128] hover:underline"><Pencil size={11} /> Edit</button>
            <button onClick={() => remove(doc)} data-testid={`cm-delete-${doc.id}`} className="p-1.5 text-slate-400 hover:text-[#C5221F]"><Trash2 size={13} /></button>
          </div>
        ))}
      </div>

      {editing && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4" onClick={() => setEditing(null)}>
          <div className="bg-white w-full max-w-2xl max-h-[88vh] overflow-y-auto p-6 lg:p-8" data-testid={`cm-editor-${type}`} onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between">
              <h3 className="font-display text-lg font-bold text-slate-900">{editing.id ? `Edit: ${editing.id}` : "New Item"}</h3>
              <button onClick={() => setEditing(null)} className="p-1.5 hover:bg-slate-100 rounded-sm"><X size={16} /></button>
            </div>
            <div className="mt-5 space-y-4">
              {schema.fields.map((f) => (
                <div key={f.key}>
                  <label className={labelCls}>{f.label}{f.required ? " *" : ""}</label>
                  <Field f={f} value={form[f.key]} onChange={set(f.key)} idPrefix={`cm-field-${type}`} />
                </div>
              ))}
            </div>
            <div className="mt-6 flex items-center gap-3 flex-wrap">
              <button onClick={() => save(false)} disabled={busy} data-testid={`cm-save-${type}`}
                className="inline-flex items-center gap-2 border border-[#0A1128] text-[#0A1128] hover:bg-slate-50 disabled:opacity-60 font-display font-semibold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors">
                {busy && <Loader2 size={13} className="animate-spin" />} Save Draft
              </button>
              <button onClick={() => save(true)} disabled={busy} data-testid={`cm-save-publish-${type}`}
                className="inline-flex items-center gap-2 bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors">
                {busy && <Loader2 size={13} className="animate-spin" />} Save & Publish
              </button>
              {editing.id && (
                <button onClick={async () => {
                  try { const { data } = await adminApi.get(`/content/${type}/${editing.id}/revisions`); setRevs(data); }
                  catch (e) { toast.error(fmtErr(e)); }
                }} data-testid={`cm-revisions-${type}`}
                  className="inline-flex items-center gap-1.5 text-[11px] font-semibold text-slate-500 hover:text-[#0A1128]">
                  <History size={12} /> Revisions
                </button>
              )}
            </div>
            {revs && (
              <div className="mt-4 border-t border-slate-100 pt-4" data-testid={`cm-revisions-list-${type}`}>
                <p className={labelCls}>Revision History</p>
                {revs.length === 0 && <p className="text-xs text-slate-500">No previous revisions.</p>}
                {revs.map((r, i) => (
                  <div key={i} className="flex items-center justify-between py-2 border-b border-slate-100 text-xs">
                    <span className="text-slate-600">{r.by} · {new Date(r.at).toLocaleString("en-IN")}</span>
                    <button onClick={async () => {
                      try { await adminApi.post(`/content/${type}/${editing.id}/restore/${i}`); toast.success("Revision restored as draft — publish when ready."); setEditing(null); load(); }
                      catch (e) { toast.error(fmtErr(e)); }
                    }} className="font-semibold text-[#C5221F] hover:underline" data-testid={`cm-restore-${i}`}>Restore</button>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

// singleton editor (home / contact / navigation_meta)
export const SingletonEditor = ({ type }) => {
  const schema = SCHEMAS_REF[type];
  const [doc, setDoc] = useState(null);
  const [form, setForm] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await adminApi.get(`/content/${type}`);
      const d = Array.isArray(data) ? data[0] : null;
      setDoc(d);
      setForm(d?.data || {});
    } catch (e) { setError(fmtErr(e)); }
  }, [type]);

  useEffect(() => { load(); }, [load]);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async (publishAfter) => {
    setBusy(true);
    try {
      await adminApi.put(`/content/${type}/${type}`, { data: form, order: 0 });
      if (publishAfter) await adminApi.post(`/content/${type}/${type}/publish`);
      toast.success(publishAfter ? "Saved and published — live on the website." : "Draft saved.");
      load();
    } catch (e) { toast.error(fmtErr(e)); } finally { setBusy(false); }
  };

  if (error) return <p className="text-xs text-[#C5221F] bg-red-50 border border-red-200 px-3 py-2.5 rounded-sm">{error}</p>;
  if (!doc) return <p className="flex items-center gap-2 text-sm text-slate-500"><Loader2 size={16} className="animate-spin" /> Loading…</p>;

  return (
    <div className="bg-white border border-slate-200 p-6 lg:p-8" data-testid={`cm-singleton-${type}`}>
      <div className="flex items-center justify-between">
        <h2 className="font-display text-xl font-bold text-slate-900">{schema.title}</h2>
        <StatusBadge status={doc.status} />
      </div>
      <div className="mt-6 grid lg:grid-cols-2 gap-4">
        {schema.fields.map((f) => (
          <div key={f.key} className={["textarea", "list", "pairs", "image"].includes(f.kind) ? "lg:col-span-2" : ""}>
            <label className={labelCls}>{f.label}</label>
            <Field f={f} value={form[f.key]} onChange={set(f.key)} idPrefix={`cm-field-${type}`} />
          </div>
        ))}
      </div>
      <div className="mt-6 flex gap-3">
        <button onClick={() => save(false)} disabled={busy} data-testid={`cm-save-${type}`}
          className="inline-flex items-center gap-2 border border-[#0A1128] text-[#0A1128] hover:bg-slate-50 disabled:opacity-60 font-display font-semibold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors">
          {busy && <Loader2 size={13} className="animate-spin" />} Save Draft
        </button>
        <button onClick={() => save(true)} disabled={busy} data-testid={`cm-save-publish-${type}`}
          className="inline-flex items-center gap-2 bg-[#C5221F] hover:bg-[#A81B18] disabled:opacity-60 text-white font-display font-semibold px-5 py-2.5 text-xs uppercase tracking-widest rounded-sm transition-colors">
          {busy && <Loader2 size={13} className="animate-spin" />} Save & Publish
        </button>
      </div>
    </div>
  );
};

export default ContentManager;
